import React, {
	useEffect,
	useMemo,
	useState,
} from "react";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import {
	Table,
	Button,
	message,
	Card,
	Layout,
	InputNumber,
	Modal,
	Form,
	Input,
	Space,
	Select,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import { ConfigProvider } from "antd";
import LeaveManageNavigation from "./navigation/LeaveManageNavigation";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Content } = Layout;
const { TextArea } = Input;
const { Option } = Select;

const MANAGE_LEAVE_BALANCE_TABLE_X = 1020;

const computeManageLeaveBalanceScrollX = () => {
	if (typeof window === "undefined") {
		return MANAGE_LEAVE_BALANCE_TABLE_X;
	}
	const pad = 56;
	return Math.min(
		MANAGE_LEAVE_BALANCE_TABLE_X,
		Math.max(520, window.innerWidth - pad),
	);
};

const ManageLeaveBalance = () => {
	const [balances, setBalances] = useState([]);
	const [allBalances, setAllBalances] = useState([]);
	const [loading, setLoading] = useState(false);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [editForm] = Form.useForm();
	const [selectedUser, setSelectedUser] = useState(null);
	const [jobFilter, setJobFilter] = useState("전체");
	const [searchText, setSearchText] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [tableScrollX, setTableScrollX] = useState(
		() => computeManageLeaveBalanceScrollX(),
	);

	useEffect(() => {
		const sync = () =>
			setTableScrollX(
				computeManageLeaveBalanceScrollX(),
			);
		sync();
		window.addEventListener("resize", sync);
		return () =>
			window.removeEventListener(
				"resize",
				sync,
			);
	}, []);

	const balanceTableProps = useMemo(
		() => ({
			scroll: {
				x: tableScrollX,
			},
			tableLayout: "fixed",
			sticky: { offsetHeader: 64 },
			style: {
				width: "100%",
			},
		}),
		[tableScrollX],
	);

	useEffect(() => {
		fetchBalances();
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
	}, []);

	const fetchBalances = async () => {
		try {
			setLoading(true);
			const response = await useAxios.get(
				"/utils/leave/balance/manage/",
			);
			if (response.status === 200) {
				setAllBalances(response.data);
				filterBalances(
					response.data,
					jobFilter,
					searchText,
				);
			}
		} catch (error) {
			message.error(
				"남은 휴가 내역을 불러오는데 실패했습니다.",
			);
			console.error("에러:", error);
		} finally {
			setLoading(false);
		}
	};

	const filterBalances = (data, job, search) => {
		let filtered = [...data];

		if (job !== "전체") {
			filtered = filtered.filter((balance) => {
				const balanceJobValue = getJobFilterValue(
					balance.user_job,
					balance.user_gender,
				);
				return balanceJobValue === job;
			});
		}

		if (search) {
			filtered = filtered.filter((balance) =>
				balance.user_name
					.toLowerCase()
					.includes(search.toLowerCase()),
			);
		}

		setBalances(filtered);
	};

	useEffect(() => {
		filterBalances(allBalances, jobFilter, searchText);
		setCurrentPage(1); // 필터 변경 시 페이지 초기화
		// eslint-disable-next-line react-hooks/exhaustive-deps -- filterBalances is stable
	}, [jobFilter, searchText, allBalances]);

	const getJobLabel = (job, gender) => {
		if (job === "목사") {
			return "목사";
		} else if (job === "전도사") {
			return gender === "남" ? "남전도사" : "여전도사";
		} else if (job === "교직원") {
			return gender === "남" ? "남직원" : "여직원";
		}
		return job || "-";
	};

	const getJobFilterValue = (job, gender) => {
		if (job === "목사") {
			return "목사";
		} else if (job === "전도사") {
			return gender === "남" ? "남전도사" : "여전도사";
		} else if (job === "교직원") {
			return gender === "남" ? "남직원" : "여직원";
		}
		return null;
	};

	const handleEdit = (record) => {
		setSelectedUser(record);
		editForm.setFieldsValue({
			monthly_leave_balance: record.monthly_leave_balance,
			monthly_menstrual_leave_balance:
				record.monthly_menstrual_leave_balance,
			deduction_reason: record.deduction_reason || "",
		});
		setEditModalVisible(true);
	};

	const handleEditSubmit = async () => {
		try {
			const values = await editForm.validateFields();
			setLoading(true);
			const response = await useAxios.put(
				`/utils/leave/balance/manage/${selectedUser.user_id}/`,
				values,
			);

			if (response.status === 200) {
				message.success(
					"남은 휴가 개수가 성공적으로 수정되었습니다.",
				);
				setEditModalVisible(false);
				editForm.resetFields();
				fetchBalances();
			}
		} catch (error) {
			if (error.errorFields) {
				message.error("입력 정보를 확인해주세요.");
			} else {
				message.error("수정 중 오류가 발생했습니다.");
				console.error("에러:", error);
			}
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			title: "이름",
			dataIndex: "user_name",
			key: "user_name",
		},
		{
			title: "직분",
			dataIndex: "classification_name",
			key: "classification_name",
		},
		{
			title: "구분",
			key: "user_job",
			render: (_, record) =>
				getJobLabel(
					record.user_job,
					record.user_gender,
				),
		},
		{
			title: "남은 월차",
			dataIndex: "monthly_leave_balance",
			key: "monthly_leave_balance",
			render: (value) => (
				<span
					style={{
						color:
							value > 0
								? "green"
								: "red",
					}}>
					{value}개
				</span>
			),
		},
		{
			title: "남은 생리휴가",
			dataIndex: "monthly_menstrual_leave_balance",
			key: "monthly_menstrual_leave_balance",
			render: (value, record) => {
				// 남자 직원의 경우 '-' 표시
				if (record.user_gender === "남") {
					return <span>-</span>;
				}
				return (
					<span
						style={{
							color:
								value > 0
									? "green"
									: "red",
						}}>
						{value}개
					</span>
				);
			},
		},
		{
			title: "차감 사유",
			dataIndex: "deduction_reason",
			key: "deduction_reason",
			render: (reason) => reason || "-",
		},
		{
			title: "관리",
			key: "action",
			render: (_, record) => (
				<Button
					type="primary"
					onClick={() => handleEdit(record)}>
					수정
				</Button>
			),
		},
	];

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="남은 월차 개수 관리" />
			<Layout className="member-layout">
				<LeaveManageNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content utils-leave-manage-layout">
						<div className="utils-rent_sanctuary__container utils-leave-manage-container page">
							<PageHeader title="남은 월차 개수 관리" />
							<Card className="surface-card utils-leave-manage-card">
								<Space
									style={{
										marginBottom: 16,
										width: "100%",
										maxWidth: "100%",
									}}
									size={[
										8,
										8,
									]}
									wrap>
									<Input
										className="utils-leave-manage-search"
										placeholder="이름으로 검색"
										prefix={
											<SearchOutlined />
										}
										value={
											searchText
										}
										onChange={(
											e,
										) =>
											setSearchText(
												e
													.target
													.value,
											)
										}
									/>
									<Select
										value={
											jobFilter
										}
										onChange={
											setJobFilter
										}
										className="utils-leave-manage-toolbar-select"
										popupMatchSelectWidth={
											false
										}>
										<Option value="전체">
											전체
										</Option>
										<Option value="목사">
											목사
										</Option>
										<Option value="남전도사">
											남전도사
										</Option>
										<Option value="여전도사">
											여전도사
										</Option>
										<Option value="남직원">
											남직원
										</Option>
										<Option value="여직원">
											여직원
										</Option>
									</Select>
								</Space>
								<Table
									{...balanceTableProps}
									columns={
										columns
									}
									dataSource={
										balances
									}
									rowKey="id"
									loading={
										loading
									}
									pagination={{
										current: currentPage,
										pageSize: 20,
										showSizeChanger: true,
										showTotal: (
											total,
										) =>
											`총 ${total}명`,
										onChange: (
											page,
										) =>
											setCurrentPage(
												page,
											),
									}}
								/>
							</Card>
						</div>
					</Content>
				</Layout>
			</Layout>

			<Modal
				title="남은 월차 개수 수정"
				open={editModalVisible}
				onOk={handleEditSubmit}
				onCancel={() => {
					setEditModalVisible(false);
					editForm.resetFields();
				}}
				confirmLoading={loading}>
				<Form form={editForm} layout="vertical">
					<Form.Item label="이름">
						<Input
							value={
								selectedUser?.user_name
							}
							disabled
						/>
					</Form.Item>
					<Form.Item
						name="monthly_leave_balance"
						label="남은 월차"
						rules={[
							{
								required: true,
								message: "남은 월차를 입력해주세요",
							},
							{
								type: "number",
								min: 0,
								message: "0 이상의 값을 입력해주세요",
							},
						]}>
						<InputNumber
							min={0}
							style={{
								width: "100%",
							}}
							addonAfter="개"
						/>
					</Form.Item>
					<Form.Item
						name="monthly_menstrual_leave_balance"
						label="남은 생리휴가"
						rules={[
							{
								required: true,
								message: "남은 생리휴가를 입력해주세요",
							},
							{
								type: "number",
								min: 0,
								message: "0 이상의 값을 입력해주세요",
							},
						]}>
						<InputNumber
							min={0}
							style={{
								width: "100%",
							}}
							addonAfter="개"
						/>
					</Form.Item>
					<Form.Item
						name="deduction_reason"
						label="차감 사유">
						<TextArea
							rows={3}
							placeholder="차감 사유를 입력하세요 (선택사항)"
						/>
					</Form.Item>
				</Form>
			</Modal>
		</ConfigProvider>
	);
};

export default ManageLeaveBalance;
